import { useMemo } from 'react';
import type { SemanticColorToken } from '@vitvio/design-system';
import { useTheme } from '@vitvio/design-system';
import { useUserContext } from '@vitvio/user-context';
import { toZonedTime, dayjs, type Nullable } from '@vitvio/helpers';
import type { JobCardContent } from '../types';

/** Layout fields derived for a job card (internal to this hook). */
interface JobCardLayout {
  shadingHeight: number;
  statusColor: SemanticColorToken;
  shadingColors: Nullable<{ bg: SemanticColorToken; stripe: SemanticColorToken }>;
}

/**
 * Derives job card layout (status color, shading height/colors) from content.
 * Used by SurgeryCard and TurnoverCard.
 */
export function useJobCard(data: Nullable<JobCardContent>): JobCardLayout {
  const { getLayout } = useTheme();
  const { hospitalTimezone, activeHospital } = useUserContext();
  const minuteHeight = getLayout('timeline', 'minuteHeight', false);
  const cardMargin = getLayout('timeline', 'cardMargin', false);
  const workdayStopHour = activeHospital.workdayStopHour;

  return useMemo(() => {
    const endTime = data?.estimatedStopTime ?? data?.plannedStop;
    const endTimeZoned = endTime
      ? (toZonedTime(endTime as Parameters<typeof toZonedTime>[0], hospitalTimezone) ?? dayjs())
      : dayjs();
    const workdayStopHourDayjs = endTimeZoned.clone().hour(workdayStopHour).minute(0).second(0).millisecond(0);
    const isPastWorkdayStopHour = endTimeZoned.isAfter(workdayStopHourDayjs);

    const duration = data?.durations?.dynamicDurationMinutes || 0;
    const status = data?.status ?? null;
    const scheduleStatus = data?.scheduleStatus;
    const shouldHaveShadingHeight = (data?.durations?.delayAfterMinutes || 0) > 0;

    const statusColor: SemanticColorToken =
      status === 'SCHEDULED' || status === 'COMPLETED'
        ? 'card.bg.muted'
        : scheduleStatus === 'DELAYED'
          ? 'card.bg.delay'
          : scheduleStatus === 'ON_TIME'
            ? 'card.bg.ontime'
            : scheduleStatus === 'AHEAD'
              ? 'card.bg.ahead'
              : 'card.bg.muted';

    let shadingHeightMinutes =
      status === 'SCHEDULED' && isPastWorkdayStopHour
        ? duration
        : status === 'RUNNING' && shouldHaveShadingHeight
          ? data?.durations?.delayAfterMinutes || 0
          : 0;
    const plannedMinutes = data?.durations?.plannedDurationMinutes ?? null;
    if (status === 'RUNNING' && plannedMinutes != null && duration < plannedMinutes) {
      shadingHeightMinutes = 0; // Job finished early: no delay shading.
    }
    // Shading must not exceed the card's duration (e.g. when delayAfterMinutes > slot length after overrides).
    shadingHeightMinutes = Math.min(shadingHeightMinutes, duration);

    const isEmphasized = isPastWorkdayStopHour && status !== 'RUNNING';
    const hasActiveShading = status === 'RUNNING' && shouldHaveShadingHeight && shadingHeightMinutes > 0;

    const shadingColors: Nullable<{ bg: SemanticColorToken; stripe: SemanticColorToken }> = isEmphasized
      ? { bg: 'card.bg.emphasized', stripe: 'card.emphasized.stripes' }
      : hasActiveShading
        ? { bg: statusColor, stripe: 'card.active.stripes' }
        : null;

    const shadingHeight = shadingHeightMinutes * minuteHeight - cardMargin;

    return {
      shadingHeight,
      statusColor,
      shadingColors,
    };
  }, [data, minuteHeight, cardMargin, hospitalTimezone, workdayStopHour]);
}
